import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import CompetitionSchema from "@/schema/WebDataManagement/MasterData/Competitions/CompetitionSchema";
import { EditCompetitionInitialValues } from "@/data/WebDataManagement/MasterData/Competitions/initial-values";
import ImageInput from "@/components/ImageInput/Index";
import LocalizedInput from "@/components/LocalizedInput";
import RenderToast from "@/components/RenderToast";
import {
  competitionGroupsMasterDataApi,
  competitionsMasterDataApi,
  sportsMasterDataApi,
} from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import { LIST_MODE } from "@/constant/Lists/listMode";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import StatusInput from "@/components/StatusInput";

const CompetitionModal = forwardRef((_, ref) => {
  const [sportId, setSportId] = useState(null);
  const [competitionGroupId, setCompetitionGroupId] = useState(null);
  const [defaultSports, setDefaultSports] = useState([]);
  const [defaultCompetitionGroups, setDefaultCompetitionGroups] = useState([]);
  const { accessToken } = useSelector((state) => state?.authReducer);

  const [loading, setLoading] = useState({
    competitionGroupsList: false,
    sportsList: false,
  });

  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${competitionsMasterDataApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Competition ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${competitionsMasterDataApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: competitionsMasterDataApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const sportslistApi = `${sportsMasterDataApi}?mode=${LIST_MODE.ALL}&filter[keyword]=`;
  const competitionGroupslistApi = `${competitionGroupsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultSports = async () => {
      setLoading((prev) => ({ ...prev, sportsList: true }));
      const response = await axiosInstance.get(sportslistApi);

      if (response) {
        const options = response.data.data.map((data) => ({
          label: `${data.code} - ${data.name}`,
          value: data.id,
        }));

        const sportId = showData?.data?.sport?.id;
        if (sportId && !options.some((opt) => opt.value === sportId)) {
          options.unshift({
            label: `${showData?.data?.sport?.code} - ${showData?.data?.sport?.name}`,
            value: showData?.data?.sport?.id,
          });
        }

        options.unshift({ label: "No sport selected", value: null });
        setDefaultSports(options);
      }

      setLoading((prev) => ({ ...prev, sportsList: false }));
    };

    const fetchDefaultParentCompetitionGroups = async () => {
      setLoading((prev) => ({
        ...prev,
        competitionGroupsList: true,
      }));
      const response = await axiosInstance.get(competitionGroupslistApi);

      if (response) {
        const options =
          response.data.data
            .filter((val) => val.id !== modalState.id) // Do not display the selected id
            .map((val) => ({
              label: `${val.code} - ${val.name}`,
              value: val.id,
            })) ?? [];

        const parentId = showData?.data?.parent?.id;
        if (parentId && !options.some((opt) => opt.value === parentId)) {
          // Add the parent to the top of the options list
          options.unshift({
            label: `${showData.data.parent.code} - ${showData.data.parent.name}`,
            value: showData.data.parent.id,
          });
        }

        options.unshift({ label: "No parent selected", value: null });
        setDefaultCompetitionGroups(options);
      }

      setLoading((prev) => ({
        ...prev,
        competitionGroupsList: false,
      }));
    };

    fetchDefaultSports();
    fetchDefaultParentCompetitionGroups();
  }, [
    accessToken,
    competitionGroupslistApi,
    modalState,
    showData,
    sportslistApi,
  ]);

  const debouncedFetchSports = debounce(async (input, callback) => {
    setLoading((prev) => ({ ...prev, sportsList: true }));
    const response = await axiosInstance.get(`${sportslistApi}${input}`);

    if (response) {
      callback(
        response.data.data.map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.id,
        })) ?? []
      );
    }

    setLoading((prev) => ({ ...prev, sportsList: false }));
  }, 500);

  const debouncedFetchParentCompetitionGroups = debounce(
    async (input, callback) => {
      setLoading((prev) => ({ ...prev, competitionGroupsList: true }));
      const response = await axiosInstance.get(
        `${competitionGroupslistApi}${input}`
      );

      if (response) {
        callback(
          response.data.data.map((item) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          })) ?? []
        );
      }

      setLoading((prev) => ({ ...prev, competitionGroupsList: false }));
    },
    500
  );

  const getSportSelectedOption = () => {
    if (!defaultSports.length) return null;

    const selectedSportId = sportId ?? formik.initialValues.sport_id;
    return defaultSports.find((opt) => opt.value === selectedSportId) || null;
  };

  const getParentCompetitionGroupSelectedOption = () => {
    if (!defaultCompetitionGroups.length) return null;

    const selectedCompetitionGroupId =
      competitionGroupId ?? formik.initialValues.competition_group_id;
    return (
      defaultCompetitionGroups.find(
        (opt) => opt.value === selectedCompetitionGroupId
      ) || null
    );
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: EditCompetitionInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: CompetitionSchema(localeCodes, !!modalState.id),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append("sport_id", values.sport_id);
    if (values.competition_group_id) {
      formData.append("competition_group_id", values.competition_group_id);
    }
    if (modalState.id) formData.append("_method", "PUT");
    if (values.icon) formData.append("icon", values.icon);
    formData.append("code", values.code);
    formData.append("status", values.status);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setSportId(null);
    setCompetitionGroupId(null);
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <div>
      <BasicModal
        header={modalState.id ? "Edit Competition" : "Add Competition"}
        onHide={closeModal}
        show={modalState.isOpen}
        saveLoading={saveIsPending}
        formik={formik}
      >
        <Row>
          <Col md={12}>
            <AsyncDropdown
              label="Sport ID"
              placeholder="No sport selected. Type to search..."
              defaultOptions={defaultSports}
              loadOptions={debouncedFetchSports}
              isLoading={loading.sportsList}
              value={getSportSelectedOption()}
              onChange={(selected) => {
                setSportId(selected?.value || null);
                formik.setFieldValue("sport_id", selected?.value || null);
              }}
              errorText={
                (formik.touched.sport_id && formik.errors.sport_id) ||
                (saveError?.status === 422 &&
                  saveError?.response?.data?.errors?.sport_id?.join(" "))
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AsyncDropdown
              required={false}
              label="Competition Group ID"
              placeholder="No parent selected. Type to search..."
              defaultOptions={defaultCompetitionGroups}
              loadOptions={debouncedFetchParentCompetitionGroups}
              isLoading={loading.competitionGroupsList}
              value={getParentCompetitionGroupSelectedOption()}
              onChange={(selected) => {
                setCompetitionGroupId(selected?.value || null);
                formik.setFieldValue(
                  "competition_group_id",
                  selected?.value || null
                );
              }}
              errorText={
                (formik.touched.competition_group_id &&
                  formik.errors.competition_group_id) ||
                (saveError?.status === 422 &&
                  saveError?.response?.data?.errors?.competition_group_id?.join(
                    " "
                  ))
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <BasicInput
              formik={formik}
              fieldName={"code"}
              saveError={saveError}
              label="ID"
            />
          </Col>
        </Row>
        <LocalizedInput
          formik={formik}
          error={saveError}
          isEdit={!!modalState.id}
        />
        <Row>
          <Col md={12}>
            <ImageInput
              formFormik={formik}
              fieldName="icon"
              label="Icon"
              error={saveError}
              existingImageUrl={modalState.id && showData?.data?.icon_url}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <StatusInput formik={formik} saveError={saveError} />
          </Col>
        </Row>
      </BasicModal>
    </div>
  );
});

export default CompetitionModal;
